import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import VendorSettingLayout from './VendorSettingLayout';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { usePageRefresh } from '../../../context/PageRefreshContext';
import { formatIstDate, formatIstDateTime } from '../../../utils/helpers/istDate';

// Vendor contract / AMC expiry reminders. A daily job on the server (09:00)
// sends what is due: on the chosen days before expiry, a weekly digest of
// what is expiring soon, and weekly after expiry until renewed. Email goes out
// only when the server has email configured; in-app notifications always do.

const PRESET_DAYS = [90, 60, 30, 15, 7, 3, 2, 1, 0];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ROLE_OPTIONS = [
    { code: 'SOCIETY_ADMIN', label: 'Society Admin' },
    { code: 'SOCIETY_MANAGER', label: 'Society Manager' },
    { code: 'COMMITTEE_CHAIRMAN', label: 'Chairman' },
    { code: 'COMMITTEE_SECRETARY', label: 'Secretary' },
    { code: 'COMMITTEE_TREASURER', label: 'Treasurer' },
    { code: 'COMMITTEE_MAINTENANCE_INCHARGE', label: 'Maintenance In-charge' },
];

const fmtDate = (v) => (v ? formatIstDate(String(v).slice(0, 10)) : '—');
const fmtDateTime = (v) => (v ? formatIstDateTime(v) : '—');
const dayLabel = (n) => (n === 0 ? 'On the day' : `${n} day${n === 1 ? '' : 's'} before`);
const slotLabel = (k) => {
    if (!k) return '';
    if (k.startsWith('D-')) return dayLabel(Number(k.slice(2)));
    if (k.startsWith('W-')) return 'Weekly digest';
    if (k.startsWith('O-')) return 'Overdue (weekly)';
    return k;
};

const Toggle = ({ on, onChange, disabled }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!on)}
        className={`tw-w-11 tw-h-6 tw-rounded-full tw-transition-colors tw-relative tw-flex-shrink-0 disabled:tw-opacity-50 ${on ? 'tw-bg-blue-600' : 'tw-bg-gray-300'}`}
    >
        <span className={`tw-absolute tw-w-5 tw-h-5 tw-bg-white tw-rounded-full tw-top-0.5 tw-transition-transform ${on ? 'tw-translate-x-5' : 'tw-translate-x-0.5'}`} />
    </button>
);

const Row = ({ title, desc, children }) => (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
        <div>
            <p className="tw-text-sm tw-font-medium tw-text-gray-700">{title}</p>
            {desc && <p className="tw-text-xs tw-text-gray-500">{desc}</p>}
        </div>
        {children}
    </div>
);

const VendorSettingNotifications = () => {
    const { can } = usePermissions();
    const canEdit = can(PERMISSIONS.VENDOR_UPDATE);
    const [settings, setSettings] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [upcoming, setUpcoming] = useState(null);
    const [logs, setLogs] = useState([]);
    const [customDay, setCustomDay] = useState('');
    const [saving, setSaving] = useState(false);
    const [running, setRunning] = useState(false);

    const load = useCallback(async () => {
        setLoadError('');
        try {
            const [s, u, l] = await Promise.all([
                axiosInstance.get(apiEndpoints.VENDOR_REMINDER_SETTINGS_API),
                axiosInstance.get(apiEndpoints.VENDOR_REMINDER_UPCOMING_API, { params: { days: 60 } }),
                axiosInstance.get(apiEndpoints.VENDOR_REMINDER_LOGS_API, { params: { limit: 30 } }),
            ]);
            setSettings(s?.data || null);
            setUpcoming(u?.data || null);
            setLogs(Array.isArray(l?.data) ? l.data : []);
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || 'Failed to load reminder settings';
            setLoadError(message);
            toast.error(message);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    usePageRefresh(load);

    const set = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));
    const toggleDay = (n) => set('days_before', settings.days_before.includes(n)
        ? settings.days_before.filter(d => d !== n)
        : [...settings.days_before, n].sort((a, b) => b - a));
    const addCustomDay = () => {
        const n = parseInt(customDay, 10);
        if (!Number.isInteger(n) || n < 0 || n > 365) return toast.warn('Enter days between 0 and 365');
        if (!settings.days_before.includes(n)) toggleDay(n);
        setCustomDay('');
    };
    const toggleRole = (code) => set('notify_roles', settings.notify_roles.includes(code)
        ? settings.notify_roles.filter(r => r !== code)
        : [...settings.notify_roles, code]);

    const save = async () => {
        setSaving(true);
        try {
            const { saved, ...body } = settings;
            const res = await axiosInstance.put(apiEndpoints.VENDOR_REMINDER_SETTINGS_API, body);
            setSettings(res?.data || settings);
            toast.success('Reminder settings saved');
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const res = await axiosInstance.post(apiEndpoints.VENDOR_REMINDER_RUN_API, {});
            const r = res?.data || {};
            const v = r.visits || {};
            const sent = (r.due?.length || 0) + (v.due?.length || 0);
            if (!r.enabled && !v.enabled) toast.info('Reminders are switched off');
            else if (!sent) toast.info((r.skipped_already_sent || v.skipped_already_sent) ? 'Today\'s reminders were already sent' : 'Nothing is due a reminder today');
            else toast.success(`Sent ${r.due?.length || 0} expiry and ${v.due?.length || 0} visit reminder(s): `
                + `${(r.emails_sent || 0) + (v.emails_sent || 0)} email(s), ${(r.in_app_sent || 0) + (v.in_app_sent || 0)} in-app`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Failed to send reminders');
        } finally {
            setRunning(false);
        }
    };

    // Recipients are shared by expiry and visit reminders.
    const allOff = settings && !settings.reminders_enabled && !settings.visit_reminders_enabled && !settings.visit_missed_alert;

    if (!settings) {
        return (
            <VendorSettingLayout>
                {loadError ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-16">
                        <p className="tw-text-sm tw-text-gray-500">{loadError}</p>
                        <button
                            type="button"
                            onClick={load}
                            className="tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-bg-blue-600 tw-text-white"
                        >
                            Retry
                        </button>
                    </div>
                ) : (
                    <div className="tw-flex tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600" />
                    </div>
                )}
            </VendorSettingLayout>
        );
    }

    const off = !settings.reminders_enabled;
    const items = upcoming?.items || [];

    return (
        <VendorSettingLayout>
            {upcoming && !upcoming.email_enabled && (
                <div className="tw-flex tw-items-start tw-gap-2 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3 tw-mb-4 tw-text-xs tw-text-amber-800">
                    <Icon icon="mdi:email-off-outline" className="tw-text-base tw-flex-shrink-0 tw-mt-0.5" />
                    <span>Email is not configured on the server, so reminders currently go out as in-app notifications only. Ask your administrator to enable email (MAIL_ENABLED and SMTP settings).</span>
                </div>
            )}

            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                {/* When */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-space-y-3">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Expiry Reminders</h2>
                    <Row title="Send expiry reminders" desc="AMC end dates and vendor contract end dates">
                        <Toggle on={settings.reminders_enabled} onChange={v => set('reminders_enabled', v)} disabled={!canEdit} />
                    </Row>
                    <Row title="AMCs" desc="Uses each AMC's end date; its own 'expiry reminder days' is added too">
                        <Toggle on={settings.include_amcs} onChange={v => set('include_amcs', v)} disabled={!canEdit || off} />
                    </Row>
                    <Row title="Vendor contracts" desc="Uses the vendor's contract end date">
                        <Toggle on={settings.include_contracts} onChange={v => set('include_contracts', v)} disabled={!canEdit || off} />
                    </Row>

                    <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                        <p className="tw-text-sm tw-font-medium tw-text-gray-700">Remind on these days before expiry</p>
                        <div className="tw-flex tw-flex-wrap tw-gap-1.5 tw-mt-2">
                            {[...new Set([...PRESET_DAYS, ...settings.days_before])].sort((a, b) => b - a).map(n => (
                                <button
                                    key={n}
                                    type="button"
                                    disabled={!canEdit || off}
                                    onClick={() => toggleDay(n)}
                                    className={`tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-border tw-transition-colors disabled:tw-opacity-50 ${settings.days_before.includes(n) ? 'tw-bg-blue-600 tw-border-blue-600 tw-text-white' : 'tw-bg-white tw-border-gray-200 tw-text-gray-600 hover:tw-bg-gray-50'}`}
                                >
                                    {dayLabel(n)}
                                </button>
                            ))}
                        </div>
                        {canEdit && !off && (
                            <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
                                <input type="number" min="0" max="365" value={customDay} onChange={e => setCustomDay(e.target.value)} placeholder="Other (days)" className="tw-w-28 tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs" />
                                <button type="button" onClick={addCustomDay} className="tw-text-xs tw-text-blue-600 hover:tw-underline">Add</button>
                            </div>
                        )}
                    </div>

                    <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg tw-space-y-2">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                            <div>
                                <p className="tw-text-sm tw-font-medium tw-text-gray-700">Weekly digest</p>
                                <p className="tw-text-xs tw-text-gray-500">Once a week, everything expiring soon</p>
                            </div>
                            <Toggle on={settings.weekly_enabled} onChange={v => set('weekly_enabled', v)} disabled={!canEdit || off} />
                        </div>
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
                            Every
                            <select value={settings.weekly_day} onChange={e => set('weekly_day', Number(e.target.value))} disabled={!canEdit || off} className="tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md">
                                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                            </select>
                            for items expiring within
                            <input type="number" min="1" max="365" value={settings.weekly_window_days} onChange={e => set('weekly_window_days', Number(e.target.value) || 1)} disabled={!canEdit || off || !settings.weekly_enabled} className="tw-w-16 tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md" />
                            days
                        </div>
                    </div>
                    <Row title="Keep reminding after expiry" desc={`Weekly on ${WEEKDAYS[settings.weekly_day]} until renewed (up to 90 days)`}>
                        <Toggle on={settings.overdue_weekly} onChange={v => set('overdue_weekly', v)} disabled={!canEdit || off} />
                    </Row>
                </div>

                {/* Vendor visits - from the monthly visit tracker, same recipients */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-space-y-3">
                    <div>
                        <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Vendor visits</h2>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">AMC service visits expected from each AMC's service schedule (Vendors &gt; Vendor Visits). Independent of the expiry reminders above.</p>
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                        <div>
                            <p className="tw-text-sm tw-font-medium tw-text-gray-700">Remind about visits still due</p>
                            <p className="tw-text-[11px] tw-text-gray-500">Once a month, for AMCs whose visit for the month has not been logged yet</p>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600 tw-flex-shrink-0">
                            on day
                            <input type="number" min="1" max="28" value={settings.visit_reminder_day ?? 20} onChange={e => set('visit_reminder_day', Math.min(Math.max(Number(e.target.value) || 1, 1), 28))} disabled={!canEdit || !settings.visit_reminders_enabled} className="tw-w-14 tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-md" />
                            <Toggle on={settings.visit_reminders_enabled} onChange={v => set('visit_reminders_enabled', v)} disabled={!canEdit} />
                        </div>
                    </div>
                    <Row title="Alert about missed visits" desc="In the first week of each month, the AMC visits missed in the previous month">
                        <Toggle on={settings.visit_missed_alert} onChange={v => set('visit_missed_alert', v)} disabled={!canEdit} />
                    </Row>
                </div>

                {/* Who */}
                <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-p-4 tw-space-y-3">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Who gets them</h2>
                    <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                        <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Roles</p>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-1.5">
                            {ROLE_OPTIONS.map(r => (
                                <label key={r.code} className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                                    <input type="checkbox" checked={settings.notify_roles.includes(r.code)} onChange={() => toggleRole(r.code)} disabled={!canEdit || allOff} />
                                    {r.label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <Row title="People assigned on the AMC" desc="Employees and committee members added to the AMC">
                        <Toggle on={settings.notify_amc_members} onChange={v => set('notify_amc_members', v)} disabled={!canEdit || allOff} />
                    </Row>
                    <Row title="The vendor" desc="Email the vendor about their own expiring AMC / contract, and a visit still due this month">
                        <Toggle on={settings.notify_vendor} onChange={v => set('notify_vendor', v)} disabled={!canEdit || allOff} />
                    </Row>
                    <div className="tw-p-3 tw-border tw-border-gray-100 tw-rounded-lg">
                        <p className="tw-text-sm tw-font-medium tw-text-gray-700">Other email addresses</p>
                        <textarea
                            rows={2}
                            value={settings.extra_emails || ''}
                            onChange={e => set('extra_emails', e.target.value)}
                            disabled={!canEdit || allOff}
                            placeholder="Comma-separated, e.g. office@society.com"
                            className="tw-w-full tw-mt-1 tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs"
                        />
                    </div>
                    {canEdit && (
                        <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-2 tw-pt-1">
                            <button onClick={runNow} disabled={running || allOff} className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-text-blue-700 tw-border tw-border-blue-200 hover:tw-bg-blue-50 disabled:tw-opacity-50" title="Send reminders that are due today, without waiting for the 9 AM run">
                                <Icon icon={running ? 'mdi:loading' : 'mdi:send-outline'} className={running ? 'mdi-spin' : ''} />
                                Send due reminders now
                            </button>
                            <button onClick={save} disabled={saving} className="tw-flex tw-items-center tw-gap-2 tw-bg-blue-600 tw-text-white tw-px-5 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50">
                                <Icon icon="mdi:content-save" />
                                {saving ? 'Saving…' : 'Save Settings'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Upcoming */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-4">
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Expiring in the next 60 days &amp; recently expired</h2>
                    <p className="tw-text-[11px] tw-text-gray-500">Reminders go out at 9 AM on the "next reminder" date.</p>
                </div>
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Vendor', 'What', 'Expiry', 'Status', 'Next reminder', 'Last sent'].map(h => (
                                    <th key={h} className="tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="tw-text-center tw-py-8 tw-text-gray-400">Nothing expiring in the next 60 days.</td></tr>
                            )}
                            {items.map(it => (
                                <tr key={`${it.item_type}-${it.item_id}-${it.expiry_date}`} className="tw-border-b tw-border-gray-50">
                                    <td className="tw-px-3 tw-py-2 tw-font-medium tw-text-gray-800">{it.vendor}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{it.item_type === 'AMC' ? it.label : 'Vendor contract'}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-whitespace-nowrap">{fmtDate(it.expiry_date)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                        <span className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${it.days_left < 0 ? 'tw-bg-rose-100 tw-text-rose-700' : it.days_left <= 7 ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                            {it.days_left < 0 ? `Expired ${-it.days_left}d ago` : it.days_left === 0 ? 'Expires today' : `${it.days_left} days left`}
                                        </span>
                                    </td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-whitespace-nowrap">{it.next_reminder ? fmtDate(it.next_reminder) : <span className="tw-text-gray-400">None scheduled</span>}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-500 tw-whitespace-nowrap">{fmtDateTime(it.last_sent)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Sent log */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-mt-4">
                <div className="tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                    <h2 className="tw-text-sm tw-font-semibold tw-text-gray-800">Reminders sent</h2>
                </div>
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                {['Sent', 'Vendor', 'What', 'Expiry', 'Reminder', 'Emails', 'In-app'].map(h => (
                                    <th key={h} className="tw-px-3 tw-py-2 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 && (
                                <tr><td colSpan={7} className="tw-text-center tw-py-8 tw-text-gray-400">No reminders sent yet.</td></tr>
                            )}
                            {logs.map(l => (
                                <tr key={l.id} className="tw-border-b tw-border-gray-50">
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-whitespace-nowrap">{fmtDateTime(l.sent_at)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-800">{l.business_name || `Vendor #${l.vendor_id}`}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{l.item_type === 'AMC' ? 'AMC' : 'Contract'}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-whitespace-nowrap">{fmtDate(l.expiry_date)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{slotLabel(l.reminder_key)}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{l.emails_sent}</td>
                                    <td className="tw-px-3 tw-py-2 tw-text-gray-600">{l.in_app_sent}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </VendorSettingLayout>
    );
};

export default VendorSettingNotifications;
